import { useState } from "react";
import type { UseFormGetValues, UseFormRegister } from "react-hook-form";
import { Link } from "react-router-dom";

import type { PasswordReset } from "~/api/Api";
import PasswordEyeClosedIcon from "~/assets/icons/PasswordEyeClosedIcon.svg?react";
import PasswordEyeOpenIcon from "~/assets/icons/PasswordEyeOpenIcon.svg?react";
import Input from "~/components/ui/input/Input";
import { PASSWORD_VALIDATION_REG_EX } from "~/helpers/helpers";
import labels from "~/helpers/labels";
import styles from "~/pages/login/Login.module.scss";
import { routes } from "~/router";

import FormLabel from "../formLabel/FormLabel";

type PasswordInputProps = {
  register: UseFormRegister<PasswordReset>;
  passwordError: string | undefined;
  forgotPasswordLink?: boolean;
  field?: "password" | "password_confirmation" | "reset_password_token";
  label?: string;
  getValues: UseFormGetValues<PasswordReset>;
  disabled?: boolean;
  autoComplete?: string;
};

function PasswordInput({
  register,
  passwordError,
  forgotPasswordLink,
  field,
  label,
  getValues,
  disabled,
  autoComplete,
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);

  const registerField:
    | "password"
    | "password_confirmation"
    | "reset_password_token" =
    (field as "password" | "password_confirmation" | "reset_password_token") ||
    "password";
  const inputLabel = label || labels.PASSWORD;

  const validate = (value: string): string | true | undefined => {
    if (registerField === "password_confirmation" && getValues().password) {
      return value === getValues().password || labels.PASSWORDS_DONT_MATCH;
    }
  };

  return (
    <>
      <div className={styles.labelsHeader}>
        <FormLabel label={inputLabel} />
        {forgotPasswordLink && (
          <Link className={styles.labelLink} to={routes.auth.forgotPassword()}>
            {labels.FORGOT_PASSWORD}
          </Link>
        )}
      </div>

      <Input
        disabled={disabled}
        autoComplete={autoComplete}
        errormessage={passwordError ?? ""}
        type={showPassword ? "text" : "password"}
        {...register(registerField, {
          required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
          validate,
          pattern: {
            value: PASSWORD_VALIDATION_REG_EX,
            message: labels.PASSWORD_VALIDATION_MESSAGE,
          },
        })}
        endAdornment={
          <div onClick={() => setShowPassword((v) => !v)}>
            {showPassword ? <PasswordEyeOpenIcon /> : <PasswordEyeClosedIcon />}
          </div>
        }
      />
    </>
  );
}

export default PasswordInput;
